import React from 'react';
import MarketingLayout from './Layouts/MarketingLayout';
import { useForm, usePage } from '@inertiajs/react';

export default function Contact() {
  const { contactDetails } = usePage().props;
  const { data, setData, post, processing, errors, reset, wasSuccessful } = useForm({
    full_name: '',
    email: '',
    phone: '',
    school_name: '',
    focus_area: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    post('/contact', {
      onSuccess: () => reset(),
    });
  };

  return (
    <MarketingLayout title="Contact Sales & Support">
      {/* Hero Header */}
      <section className="spx-hero" style={{ paddingBottom: '32px' }}>
        <div className="spx-container animate-fade-in">
          <span className="spx-badge spx-badge-blue" style={{ marginBottom: '16px' }}>Get in touch</span>
          <h1 className="spx-title-large">We are here to support your school</h1>
          <p className="spx-body-lead" style={{ maxWidth: '640px', margin: '0 auto' }}>
            Have questions about pricing, rollout mapping, or training? Drop us a message, and our customer success team will get back to you shortly.
          </p>
        </div>
      </section>

      {/* Main Grid */}
      <section className="spx-section" style={{ backgroundColor: 'var(--bg-secondary)', borderTop: '1px solid var(--border-primary)' }}>
        <div className="spx-container">
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.5fr', gap: '48px' }}>
            
            {/* Contact details card */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '32px' }}>
              <div className="spx-glass-card">
                <h3 className="spx-title-small" style={{ fontSize: '20px', marginBottom: '24px' }}>Contact Information</h3>
                
                <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--accent-blue-light)', color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                      <i className="fas fa-phone"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: '700', textTransform: 'uppercase' }}>Phone Call</div>
                      <a href={`tel:${contactDetails?.phone || '+2348144676666'}`} style={{ color: 'var(--text-primary)', textDecoration: 'none', fontWeight: '600' }}>
                        {contactDetails?.phone || '+234 814 467 6666'}
                      </a>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--accent-blue-light)', color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                      <i className="fas fa-envelope"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: '700', textTransform: 'uppercase' }}>Email Address</div>
                      <a href={`mailto:${contactDetails?.email || 'hello@schoolplex.ng'}`} style={{ color: 'var(--text-primary)', textDecoration: 'none', fontWeight: '600' }}>
                        {contactDetails?.email || 'hello@schoolplex.ng'}
                      </a>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
                    <div style={{ width: '40px', height: '40px', borderRadius: '50%', backgroundColor: 'var(--accent-blue-light)', color: 'var(--accent-blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '16px' }}>
                      <i className="fab fa-whatsapp"></i>
                    </div>
                    <div>
                      <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', fontWeight: '700', textTransform: 'uppercase' }}>WhatsApp Chat</div>
                      <a href={contactDetails?.whatsapp || "https://wa.me/2348144676666"} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--text-primary)', textDecoration: 'none', fontWeight: '600' }}>
                        Chat with Sales Engineer
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              <div className="spx-glass-card" style={{ background: 'var(--accent-blue-light)', borderColor: 'var(--accent-blue)' }}>
                <h4 className="spx-title-small" style={{ fontSize: '16px', marginBottom: '8px', color: 'var(--text-primary)' }}>Looking for a quick sandbox?</h4>
                <p style={{ fontSize: '14px', color: 'var(--text-secondary)', margin: '0 0 16px 0', lineHeight: '1.5' }}>
                  If you want to immediately see how the platform functions, skip the form and create a trial account now.
                </p>
                <a href="/onboarding" className="spx-btn spx-btn-primary" style={{ width: '100%' }}>
                  Create Free Trial Portal
                </a>
              </div>
            </div>

            {/* Submission Form */}
            <div className="spx-glass-card">
              <h3 className="spx-title-small" style={{ fontSize: '20px', marginBottom: '24px' }}>Send us a message</h3>
              
              <form onSubmit={handleSubmit}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div className="spx-form-group" style={{ margin: 0 }}>
                    <label htmlFor="full_name" className="spx-label">Full Name *</label>
                    <input 
                      type="text" 
                      id="full_name" 
                      className="spx-input" 
                      placeholder="e.g. John Doe"
                      value={data.full_name}
                      onChange={(e) => setData('full_name', e.target.value)}
                      required
                    />
                    {errors.full_name && <span style={{ color: 'var(--accent-pink)', fontSize: '12px' }}>{errors.full_name}</span>}
                  </div>

                  <div className="spx-form-group" style={{ margin: 0 }}>
                    <label htmlFor="email" className="spx-label">Email Address *</label>
                    <input 
                      type="email" 
                      id="email" 
                      className="spx-input" 
                      placeholder="e.g. john@school.com"
                      value={data.email}
                      onChange={(e) => setData('email', e.target.value)}
                      required
                    />
                    {errors.email && <span style={{ color: 'var(--accent-pink)', fontSize: '12px' }}>{errors.email}</span>}
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                  <div className="spx-form-group" style={{ margin: 0 }}>
                    <label htmlFor="phone" className="spx-label">Phone Number</label>
                    <input 
                      type="tel" 
                      id="phone" 
                      className="spx-input" 
                      placeholder="e.g. +234 800 000 0000"
                      value={data.phone}
                      onChange={(e) => setData('phone', e.target.value)}
                    />
                    {errors.phone && <span style={{ color: 'var(--accent-pink)', fontSize: '12px' }}>{errors.phone}</span>}
                  </div>

                  <div className="spx-form-group" style={{ margin: 0 }}>
                    <label htmlFor="school_name" className="spx-label">School Name</label>
                    <input 
                      type="text" 
                      id="school_name" 
                      className="spx-input" 
                      placeholder="e.g. Alpha Academy"
                      value={data.school_name}
                      onChange={(e) => setData('school_name', e.target.value)}
                    />
                    {errors.school_name && <span style={{ color: 'var(--accent-pink)', fontSize: '12px' }}>{errors.school_name}</span>}
                  </div>
                </div>

                <div className="spx-form-group">
                  <label htmlFor="focus_area" className="spx-label">What is your main operational bottleneck?</label>
                  <select 
                    id="focus_area" 
                    className="spx-select"
                    value={data.focus_area}
                    onChange={(e) => setData('focus_area', e.target.value)}
                  >
                    <option value="">Select an area...</option>
                    <option value="Results & Reports">Results & Report Sheet processing</option>
                    <option value="Fees & Payments">School Fees tracking and collections</option>
                    <option value="CBT & Exams">Computer Based Testing (CBT)</option>
                    <option value="School Website">Professional School Website</option>
                    <option value="Other">General consultation / Other</option>
                  </select>
                  {errors.focus_area && <span style={{ color: 'var(--accent-pink)', fontSize: '12px' }}>{errors.focus_area}</span>}
                </div>

                <div className="spx-form-group">
                  <label htmlFor="message" className="spx-label">Message *</label>
                  <textarea 
                    id="message" 
                    className="spx-textarea" 
                    placeholder="How can we help your school this academic term?"
                    value={data.message}
                    onChange={(e) => setData('message', e.target.value)}
                    required
                  ></textarea>
                  {errors.message && <span style={{ color: 'var(--accent-pink)', fontSize: '12px' }}>{errors.message}</span>}
                </div>

                <button 
                  type="submit" 
                  className="spx-btn spx-btn-primary" 
                  style={{ width: '100%', padding: '14px 24px' }}
                  disabled={processing}
                >
                  {processing ? 'Submitting request...' : 'Send Message'}
                </button>
              </form>
            </div>

          </div>
        </div>
      </section>
    </MarketingLayout>
  );
}
